import React, {
	createContext,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Animated,
	LayoutChangeEvent,
	NativeScrollEvent,
	NativeSyntheticEvent,
	Platform,
	ScrollView,
	StyleProp,
	View,
	ViewStyle,
} from 'react-native';

import { useSafeViewport } from '../hooks/useSafeViewport';
import {
	getFocusedInput,
	getKeyboard,
	KeyboardFrame,
	Measurable,
	measureOnScreen,
	subscribeKeyboard,
	WindowRect,
} from './keyboardFrame';

/**
 * Keeps the focused text field above the on-screen keyboard, the same way on iOS, Android and the
 * mobile web.
 *
 * - `KeyboardLift` wraps something that floats - a modal's panel - and slides it up with the
 *   keyboard, just far enough that the field (and, when there is room, its submit button) clears
 *   it, never further than the top of the safe area unless the field itself needs it.
 * - `useKeyboardScrollArea` makes a scroll view keyboard aware: while the keyboard covers its
 *   bottom it gives up that part of its height, so everything can still be scrolled to above the
 *   keyboard, and it scrolls the field into view. Inside a `KeyboardLift` the two work together:
 *   the panel rises first and the scroll view takes care of what is still covered.
 *
 * Both put everything back as the keyboard closes.
 */

/** Room kept between the field (or button) and the keyboard. */
const KEYBOARD_GAP = 12;
/** Room kept under the top of the safe area when a panel rises. */
const TOP_GAP = 8;
/** How often native checks which field has focus while the keyboard is up - it has no event. */
const FOCUS_POLL_MS = 150;

type ScrollAreaHandle = {
	frame: RefObject<View | null>;
	submit?: RefObject<View | null>;
	/** Cover `overlap` of the area's bottom and scroll by `delta`, with the lift already applied. */
	apply: (overlap: number, delta: number, keyboard: KeyboardFrame) => void;
	reset: (keyboard: KeyboardFrame) => void;
};

type Avoider = { register: (area: ScrollAreaHandle) => () => void };

const KeyboardAvoiderContext = createContext<Avoider | null>(null);

type LiftHandle = {
	outer: RefObject<View | null>;
	inner: RefObject<View | null>;
	submit?: RefObject<View | null>;
	bounds?: RefObject<View | null>;
	moveTo: (lift: number, keyboard: KeyboardFrame) => void;
};

const contains = (outer: WindowRect, inner: WindowRect) =>
	inner.x >= outer.x - 1 &&
	inner.x + inner.width <= outer.x + outer.width + 1 &&
	inner.y >= outer.y - 1 &&
	inner.y + inner.height <= outer.y + outer.height + 1;

const bottomOf = (rect: WindowRect) => rect.y + rect.height;

/** How far to scroll so `field` (and `submit`, when both fit) sit between `top` and `bottom`. */
const scrollDelta = (field: WindowRect, submit: WindowRect | null, top: number, bottom: number) => {
	if (field.y < top) return field.y - top;
	let delta = Math.max(0, bottomOf(field) - bottom);
	if (submit && field.y - (bottomOf(submit) - bottom) >= top) {
		delta = Math.max(delta, bottomOf(submit) - bottom);
	}
	// A field taller than the room left shows its top, where the caret starts.
	return Math.min(delta, field.y - top);
};

/**
 * The shared engine: on every keyboard change, and on every change of focus while it is up, works
 * out where the focused field is and moves the lift and the scroll areas so it can be seen.
 */
const useAvoider = (lift: LiftHandle | null, enabled: boolean): Avoider => {
	const areas = useRef(new Set<ScrollAreaHandle>()).current;
	const { insets } = useSafeViewport();
	const safeTop = insets.top + TOP_GAP;
	const state = useRef({ run: 0, focused: null as unknown, lifted: false, safeTop });
	state.current.safeTop = safeTop;
	const liftRef = useRef(lift);
	liftRef.current = lift;

	const avoid = useCallback(async () => {
		const run = ++state.current.run;
		const keyboard = getKeyboard();
		const currentLift = liftRef.current;

		if (keyboard.height <= 0) {
			state.current.focused = null;
			if (state.current.lifted) currentLift?.moveTo(0, keyboard);
			state.current.lifted = false;
			areas.forEach(area => area.reset(keyboard));
			return;
		}

		const input = getFocusedInput();
		state.current.focused = input;
		if (!input) return;

		const areaList = [...areas];
		const [outer, inner, bounds, liftSubmit, field, ...areaRects] = await Promise.all([
			measureOnScreen(currentLift?.outer.current as Measurable),
			measureOnScreen(currentLift?.inner.current as Measurable),
			measureOnScreen(currentLift?.bounds?.current as Measurable),
			measureOnScreen(currentLift?.submit?.current as Measurable),
			measureOnScreen(input),
			...areaList.flatMap(area => [
				measureOnScreen(area.frame.current as Measurable),
				measureOnScreen(area.submit?.current as Measurable),
			]),
		]);
		if (run !== state.current.run || !field) return;

		// Where everything sits with the lift undone: the outer view never moves, so the gap between
		// it and the inner one is the lift in effect, whether or not measuring counts transforms.
		const shift = outer && inner ? inner.y - outer.y : 0;
		const unshift = (rect: WindowRect | null) => rect && { ...rect, y: rect.y - shift };
		const field0 = currentLift ? unshift(field)! : field;
		const box = unshift(bounds ?? inner);
		if (currentLift && (!box || !contains(box, field0))) return;

		const index = areaList.findIndex((_, i) => {
			const rect = areaRects[i * 2];
			return rect != null && contains(currentLift ? unshift(rect)! : rect, field0);
		});
		const area = index >= 0 ? areaList[index] : null;
		const area0 = area
			? currentLift
				? unshift(areaRects[index * 2])
				: areaRects[index * 2]
			: null;
		const areaSubmit0 = area
			? currentLift
				? unshift(areaRects[index * 2 + 1])
				: areaRects[index * 2 + 1]
			: null;

		const visibleBottom = keyboard.top - KEYBOARD_GAP;
		let lifted = 0;
		if (currentLift && box) {
			const maxLift = Math.max(0, box.y - state.current.safeTop);
			if (area && area0) {
				// Raise the whole scroll area clear if the panel can go that far; it scrolls the rest.
				lifted = Math.min(Math.max(0, bottomOf(area0) - keyboard.top), maxLift);
			} else {
				const fieldNeed = bottomOf(field0) - visibleBottom;
				const submit0 = unshift(liftSubmit);
				const submitNeed = submit0 ? bottomOf(submit0) - visibleBottom : -Infinity;
				lifted =
					submitNeed > fieldNeed && field0.y - submitNeed >= state.current.safeTop
						? submitNeed
						: fieldNeed;
				// The field itself must show, even if the panel's top has to leave the screen for it.
				lifted = Math.min(Math.max(0, lifted), Math.max(maxLift, fieldNeed));
			}
			currentLift.moveTo(lifted, keyboard);
			state.current.lifted = lifted > 0;
		}

		areaList.forEach(other => other !== area && other.reset(keyboard));
		if (area && area0) {
			const raise = (rect: WindowRect | null) => rect && { ...rect, y: rect.y - lifted };
			const overlap = Math.max(0, bottomOf(area0) - lifted - keyboard.top);
			const top = Math.max(area0.y - lifted, state.current.safeTop) + KEYBOARD_GAP / 2;
			const bottom = Math.min(visibleBottom, bottomOf(area0) - lifted - KEYBOARD_GAP);
			area.apply(overlap, scrollDelta(raise(field0)!, raise(areaSubmit0), top, bottom), keyboard);
		}
	}, [areas]);

	useEffect(() => {
		if (!enabled) return;
		const unsubscribe = subscribeKeyboard(avoid);
		// Native has no focus event to listen to; while the keyboard is up, check for a new field.
		const poll =
			Platform.OS === 'web'
				? null
				: setInterval(() => {
						if (getKeyboard().height > 0 && getFocusedInput() !== state.current.focused) avoid();
					}, FOCUS_POLL_MS);
		const onFocus = () => setTimeout(avoid, 0);
		if (Platform.OS === 'web') document.addEventListener('focusin', onFocus);
		return () => {
			unsubscribe();
			if (poll) clearInterval(poll);
			if (Platform.OS === 'web') document.removeEventListener('focusin', onFocus);
		};
	}, [avoid, enabled]);

	return useMemo(
		() => ({
			register: (area: ScrollAreaHandle) => {
				areas.add(area);
				return () => {
					areas.delete(area);
				};
			},
		}),
		[areas],
	);
};

type KeyboardLiftProps = {
	children: React.ReactNode;
	/** Style of the outer view, which keeps the place the content has with the keyboard closed. */
	style?: StyleProp<ViewStyle>;
	/** Style of the inner view, the one that moves. */
	contentStyle?: StyleProp<ViewStyle>;
	/** A button to bring above the keyboard with the field, when both fit. */
	submitRef?: RefObject<View | null>;
	/**
	 * The panel inside, for a lift that spans the screen: only fields in it are lifted for, and
	 * it rises no further than to bring its top to the top of the safe area. Defaults to the
	 * lift's own frame.
	 */
	boundsRef?: RefObject<View | null>;
	pointerEvents?: 'box-none' | 'auto';
	/**
	 * The value the content is translated by (0 or less), for a caller that needs to follow the
	 * lift - e.g. to hold a backdrop still behind the moving panel.
	 */
	translateY?: Animated.Value;
};

/** Slides what it wraps up with the keyboard so the focused field inside stays in sight. */
export const KeyboardLift = ({
	children,
	style,
	contentStyle,
	submitRef,
	boundsRef,
	pointerEvents = 'box-none',
	translateY: givenTranslateY,
}: KeyboardLiftProps) => {
	const outer = useRef<View>(null);
	const inner = useRef<View>(null);
	const ownTranslateY = useRef(new Animated.Value(0)).current;
	const translateY = givenTranslateY ?? ownTranslateY;

	const handle = useMemo<LiftHandle>(
		() => ({
			outer,
			inner,
			submit: submitRef,
			bounds: boundsRef,
			moveTo: (lift, keyboard) =>
				Animated.timing(translateY, {
					toValue: -lift,
					duration: keyboard.duration,
					easing: keyboard.easing,
					useNativeDriver: Platform.OS !== 'web',
				}).start(),
		}),
		[boundsRef, submitRef, translateY],
	);
	const avoider = useAvoider(handle, true);

	return (
		<KeyboardAvoiderContext.Provider value={avoider}>
			<View ref={outer} style={style} pointerEvents={pointerEvents} collapsable={false}>
				<Animated.View
					ref={inner}
					style={[contentStyle, { transform: [{ translateY }] }]}
					pointerEvents={pointerEvents}
					collapsable={false}
				>
					{children}
				</Animated.View>
			</View>
		</KeyboardAvoiderContext.Provider>
	);
};

export type KeyboardScrollOptions = {
	/** A button to scroll into view with the field, when both fit. */
	submitRef?: RefObject<View | null>;
};

/**
 * Makes a scroll view keyboard aware. Spread `frameStyle` on the view framing the scroll view,
 * attach `scrollRef` and pass `onScroll` / `onLayout` through. Pass `enabled: false` for a scroll
 * view that holds no text field; it then costs nothing.
 */
export const useKeyboardScrollArea = (enabled: boolean, options: KeyboardScrollOptions = {}) => {
	const frame = useRef<View>(null);
	const scrollRef = useRef<ScrollView>(null);
	const offset = useRef(0);
	const restoreTo = useRef<number | null>(null);
	const pendingDelta = useRef<number | null>(null);
	const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [inset, setInset] = useState(0);
	const insetRef = useRef(0);

	const parent = useContext(KeyboardAvoiderContext);
	// Outside a lift, the scroll area runs the engine on its own.
	const ownAvoider = useAvoider(null, enabled && !parent);
	const avoider = parent ?? ownAvoider;

	const scrollBy = useCallback((delta: number) => {
		if (Math.abs(delta) < 1) return;
		const y = Math.max(0, offset.current + delta);
		offset.current = y;
		scrollRef.current?.scrollTo({ y, animated: true });
	}, []);

	const handle = useMemo<ScrollAreaHandle>(
		() => ({
			frame,
			submit: options.submitRef,
			apply: (overlap, delta) => {
				if (resetTimer.current) clearTimeout(resetTimer.current);
				resetTimer.current = null;
				if (restoreTo.current === null) restoreTo.current = offset.current;
				const next = Math.round(overlap);
				if (next === insetRef.current) {
					scrollBy(delta);
					return;
				}
				// A scroll past the old end would stop short; wait for the shorter frame's layout.
				pendingDelta.current = delta;
				insetRef.current = next;
				setInset(next);
			},
			reset: keyboard => {
				if (restoreTo.current === null && insetRef.current === 0) return;
				pendingDelta.current = null;
				if (restoreTo.current !== null) {
					offset.current = restoreTo.current;
					scrollRef.current?.scrollTo({ y: restoreTo.current, animated: true });
					restoreTo.current = null;
				}
				// Give the room back once the keyboard has gone, so nothing jumps while it slides away.
				if (resetTimer.current) clearTimeout(resetTimer.current);
				resetTimer.current = setTimeout(() => {
					resetTimer.current = null;
					insetRef.current = 0;
					setInset(0);
				}, keyboard.duration);
			},
		}),
		[options.submitRef, scrollBy],
	);

	useEffect(() => {
		if (!enabled) return;
		const unregister = avoider.register(handle);
		return () => {
			unregister();
			if (resetTimer.current) clearTimeout(resetTimer.current);
		};
	}, [avoider, enabled, handle]);

	const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
		offset.current = event.nativeEvent.contentOffset.y;
	}, []);

	const onLayout = useCallback(
		(_event: LayoutChangeEvent) => {
			const delta = pendingDelta.current;
			if (delta === null) return;
			pendingDelta.current = null;
			scrollBy(delta);
		},
		[scrollBy],
	);

	return {
		frame,
		scrollRef,
		onScroll,
		onLayout,
		frameStyle: inset > 0 ? { paddingBottom: inset } : null,
	};
};
